import {
  FilterableSurveyResult,
  SecurityCultureSurvey,
} from '@/client/security-culture/types';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { DoughnutChart } from '@/ui/charts/doughnut-chart';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CardInfo } from './CardInfo';

const StyledCardContainer = styled(FlexContainer)<{ isDemo?: boolean }>`
  background-color: ${({ isDemo }) =>
    isDemo ? 'var(--purple-background)' : 'var(--beige-main)'};
  padding: var(--default-padding) var(--big-padding);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;

  &.stretch-children {
    .children {
      overflow-y: hidden;
      overflow-x: auto;
      padding-bottom: var(--medium-padding);

      > div {
        flex: 1;

        > div {
          width: 100%;
        }
      }
    }
  }

  .demo-overlay {
    position: relative;
    ${({ isDemo }) =>
      isDemo &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--purple-background);
      opacity: 0.5;
      z-index: 1;
    }
  `}
  }
`;

const StyledTableDiv = styled.div`
  width: 100%;

  .table-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: baseline;
    border-bottom: 1px solid var(--gray-dark);
    padding: var(--xsmall-padding) 0;
    gap: var(--medium-padding);

    &:last-child {
      border-bottom: none;
    }

    .table-text {
      min-width: max-content;
      font-weight: 500;
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
    }

    .table-count {
      text-align: end;
      font-weight: 700;
      font-size: var(--body-font-size);
      line-height: var(--body-line-height);
    }
  }
`;

type SCParticipationProps = {
  survey?: SecurityCultureSurvey;
  filterableSurvey?: FilterableSurveyResult;
  isDemo?: boolean;
};

export const SCParticipation: React.FC<SCParticipationProps> = ({
  survey,
  filterableSurvey,
  isDemo,
}) => {
  const { t } = useTranslation();
  const totalCount =
    survey?.totalParticipants ?? filterableSurvey?.totalCount ?? 0;
  const hasNoData = !isDemo && totalCount === 0;

  return (
    <StyledCardContainer
      id="sc-participation-card"
      direction="column"
      gap={24}
      justify="flex-start"
      flex="0.3"
      isDemo={isDemo}
    >
      <CardInfo
        id="security-culture-participation"
        title={t('security.culture.participation')}
        tooltip={t('security.culture.participation.tooltip')}
        redTitle={isDemo ? t('security.culture.notMeasured') : undefined}
        isDemo={isDemo}
      />
      {hasNoData ? (
        <ChartEmptyState
          type={ChartEmptyStateType.SC}
          noBorder
          noMargin
          transparent
          minHeight={200}
          title={t('security.culture.participation.empty.title')}
          description={t('security.culture.participation.empty.description')}
        />
      ) : (
        <FlexContainer direction="column" className="mt-5" gap={32}>
          <div className="demo-overlay">
            <DoughnutChart
              allCount={
                survey?.totalParticipants || filterableSurvey?.totalCount || 0
              }
              singleCount={
                survey?.participated || filterableSurvey?.participantsCount || 0
              }
              id="sc-participation"
              cutout="70%"
              width="200px"
              isPercentCount
              isDashboard
              isPurple
            />
          </div>
          <StyledTableDiv>
            <div className="table-row">
              <span className="table-text">
                {t('security.culture.participated.employees')}
              </span>
              <span className="table-count">
                {survey?.participated ||
                  filterableSurvey?.participantsCount ||
                  0}
              </span>
            </div>
            <div className="table-row">
              <span className="table-text">
                {t('security.culture.all.employees')}
              </span>
              <span className="table-count">
                {survey?.totalParticipants || filterableSurvey?.totalCount || 0}
              </span>
            </div>
          </StyledTableDiv>
        </FlexContainer>
      )}
    </StyledCardContainer>
  );
};
